<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import QuotesAPI from 'dashboard/api/planning/quotes';

const props = defineProps({
  /** Format: `${eventId}/${quoteId}` from `__PANEL__:quote:…` */
  quoteRef: { type: String, required: true },
  accountId: { type: [String, Number], required: true },
});

const { t } = useI18n();

const loading = ref(true);
const error = ref(null);
const quote = ref(null);

const parts = computed(() => {
  const [eventId, quoteId] = String(props.quoteRef || '').split('/');
  return { eventId, quoteId };
});

const previewUrl = computed(() => quote.value?.preview_url || null);
const title = computed(() => {
  if (!quote.value) return t('COPILOT.PANEL.QUOTE');
  return quote.value.name || `Quote v${quote.value.version || 1}`;
});

async function loadQuote() {
  const { eventId, quoteId } = parts.value;
  if (!eventId || !quoteId) {
    error.value = t('COPILOT.PANEL.QUOTE_LOAD_ERROR');
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    const { data } = await QuotesAPI.get(eventId, quoteId);
    quote.value = data;
    if (!data?.preview_url) {
      error.value = t('COPILOT.PANEL.QUOTE_PREVIEW_MISSING');
    }
  } catch (e) {
    quote.value = null;
    error.value = t('COPILOT.PANEL.QUOTE_LOAD_ERROR');
  } finally {
    loading.value = false;
  }
}

onMounted(loadQuote);
watch(
  () => props.quoteRef,
  () => loadQuote()
);
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden bg-n-solid-2">
    <div
      class="flex items-center justify-between gap-3 px-4 py-3 border-b border-n-weak bg-n-solid-1 shrink-0"
    >
      <div class="min-w-0">
        <div class="text-sm font-semibold text-n-slate-12 truncate">
          {{ title }}
        </div>
        <div
          v-if="quote"
          class="mt-0.5 text-xs text-n-slate-11 flex flex-wrap gap-x-3 gap-y-0.5"
        >
          <span v-if="quote.version != null">v{{ quote.version }}</span>
          <span v-if="quote.status" class="capitalize">{{
            String(quote.status).replace(/_/g, ' ')
          }}</span>
        </div>
      </div>
      <a
        v-if="previewUrl"
        :href="previewUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2 shrink-0"
      >
        <span class="i-lucide-external-link size-3.5" />
        {{ t('COPILOT.PANEL.OPEN') }}
      </a>
    </div>

    <div
      v-if="loading"
      class="flex-1 flex items-center justify-center text-sm text-n-slate-11"
    >
      {{ t('COPILOT.PANEL.LOADING') }}
    </div>
    <div
      v-else-if="error"
      class="flex-1 flex items-center justify-center px-6 text-sm text-n-ruby-11 text-center"
    >
      {{ error }}
    </div>
    <div v-else-if="previewUrl" class="flex-1 min-h-0 p-3">
      <div
        class="h-full rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
      >
        <iframe
          :src="previewUrl"
          class="w-full h-full border-0 bg-white"
          title="Quote preview"
        />
      </div>
    </div>
  </div>
</template>
